import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../constants/helpRouteMap";
import {
  bulkDeleteTags,
  createTag,
  deleteTag,
  getTags,
  mergeTags,
  previewMergeTags,
  Tag,
  TagsMergePreview,
  updateTag,
} from "../../helpers/audience_helper";

const PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 350;

/**
 * Listagem de tags do tenant — i18n tri-locale (LAC-021).
 * Por quê: a API pagina; sem meta/search a UI truncava. Selects em Contatos/disparo
 * usam fetchAllTags (não há cache HTTP — era só a 1ª página de 100).
 */
const TagsList = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  document.title = t("tags.documentTitle");
  const helpHref = helpUrlForRoute("/tags", i18n.language || "pt-BR");

  const searchFromUrl = searchParams.get("search") || "";

  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(searchFromUrl);
  const [appliedSearch, setAppliedSearch] = useState(searchFromUrl);
  const [sort, setSort] = useState<"name" | "usage">("name");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<number | "">("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Tag | null>(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Tag | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [mergePreviewData, setMergePreviewData] =
    useState<TagsMergePreview | null>(null);
  const [mergePreviewLoading, setMergePreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTags({
        page,
        per_page: PER_PAGE,
        search: appliedSearch || undefined,
        sort,
      });
      if (!res?.success) {
        setError(t("tags.error.load"));
        setTags([]);
        return;
      }
      const lp = res.meta?.last_page ?? 1;
      setTags(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(lp);
      if (page > lp && lp >= 1) {
        setPage(lp);
      }
    } catch (e: any) {
      setError(e?.message || t("tags.error.load"));
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch, sort, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounce + sync ?search= na URL (compartilhável / deep-link).
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = search.trim();
      setAppliedSearch((prev) => {
        if (prev === next) {
          return prev;
        }
        setPage(1);
        return next;
      });
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          if (next) {
            nextParams.set("search", next);
          } else {
            nextParams.delete("search");
          }
          return nextParams;
        },
        { replace: true }
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search, setSearchParams]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setFieldError(null);
    setModalOpen(true);
  };

  const openEdit = (tag: Tag) => {
    setEditing(tag);
    setName(tag.name);
    setFieldError(null);
    setModalOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFieldError(null);
    try {
      const trimmed = name.trim();
      const res = editing
        ? await updateTag(editing.id, { name: trimmed })
        : await createTag({ name: trimmed });
      if (!res?.success) {
        setFieldError(
          editing ? t("tags.error.rename") : t("tags.error.create")
        );
        return;
      }
      setModalOpen(false);
      setEditing(null);
      setName("");
      setSuccess(
        editing ? t("tags.success.renamed") : t("tags.success.created")
      );
      if (!editing && page !== 1) {
        setPage(1);
      } else {
        await load();
      }
    } catch (err: any) {
      const details =
        err?.details?.name || err?.response?.data?.error?.details?.name;
      setFieldError(
        Array.isArray(details)
          ? details[0]
          : err?.message ||
              (editing ? t("tags.error.rename") : t("tags.error.create"))
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeletingBusy(true);
    try {
      await deleteTag(deleting.id);
      setSuccess(t("tags.success.deleted"));
      setDeleting(null);
      await load();
    } catch (err: any) {
      setError(err?.message || t("tags.error.delete"));
      setDeleting(null);
    } finally {
      setDeletingBusy(false);
    }
  };

  const toggleSelected = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((x) => x !== id);
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(tags.map((tag) => tag.id));
  };

  const runBulkDelete = async () => {
    if (selectedIds.length === 0) {
      return;
    }
    if (
      !window.confirm(
        t("tags.bulkDelete.confirm", { count: selectedIds.length })
      )
    ) {
      return;
    }
    setBulkBusy(true);
    try {
      await bulkDeleteTags({ ids: selectedIds });
      setSuccess(t("tags.success.bulkDeleted"));
      setSelectedIds([]);
      await load();
    } catch (err: any) {
      setError(err?.message || t("tags.error.delete"));
    } finally {
      setBulkBusy(false);
    }
  };

  const runMerge = async () => {
    if (selectedIds.length < 2 || mergeTargetId === "") {
      setError(t("tags.merge.needSelection"));
      return;
    }
    const targetId = Number(mergeTargetId);
    const sourceIds = selectedIds.filter((id) => id !== targetId);
    if (sourceIds.length === 0) {
      setError(t("tags.merge.needSources"));
      return;
    }
    setBulkBusy(true);
    try {
      await mergeTags({ source_ids: sourceIds, target_id: targetId });
      setSuccess(t("tags.success.merged"));
      setMergeOpen(false);
      setMergeTargetId("");
      setSelectedIds([]);
      await load();
    } catch (err: any) {
      setError(err?.message || t("tags.error.merge"));
    } finally {
      setBulkBusy(false);
    }
  };

  const hasSearch = appliedSearch !== "";
  const hasSelection = selectedIds.length > 0;

  const selectedTagsOnPage = useMemo(
    () => tags.filter((tag) => selectedIds.includes(tag.id)),
    [tags, selectedIds]
  );

  const mergeTargetTag = useMemo(() => {
    if (mergeTargetId === "") return null;
    return (
      selectedTagsOnPage.find((tag) => tag.id === Number(mergeTargetId)) ?? null
    );
  }, [mergeTargetId, selectedTagsOnPage]);

  useEffect(() => {
    if (!mergeOpen || mergeTargetId === "") {
      setMergePreviewData(null);
      return;
    }
    const targetId = Number(mergeTargetId);
    const sourceIds = selectedIds.filter((id) => id !== targetId);
    if (sourceIds.length === 0) {
      setMergePreviewData(null);
      return;
    }

    let cancelled = false;
    setMergePreviewLoading(true);
    void previewMergeTags({ source_ids: sourceIds, target_id: targetId })
      .then((res) => {
        if (!cancelled) {
          setMergePreviewData(res.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMergePreviewData(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setMergePreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [mergeOpen, mergeTargetId, selectedIds]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("tags.title")}
          actions={<PageHelpLink href={helpHref} label={t("tags.help")} />}
        />
        <DismissiblePageTip tipId="tags">{t("tags.tip")}</DismissiblePageTip>
        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert color="success" toggle={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span className="fw-medium">{t("tags.cardTitle")}</span>
                <Button color="primary" onClick={openCreate}>
                  {t("tags.new")}
                </Button>
              </CardHeader>
              <CardBody>
                <Row className="g-2 mb-3">
                  <Col md={4}>
                    <Input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={t("tags.search.placeholder")}
                      aria-label={t("tags.search.placeholder")}
                    />
                  </Col>
                  <Col md={3}>
                    <Input
                      type="select"
                      value={sort}
                      onChange={(e) => {
                        setSort(e.target.value === "usage" ? "usage" : "name");
                        setPage(1);
                      }}
                      aria-label={t("tags.sort.label")}
                    >
                      <option value="name">{t("tags.sort.name")}</option>
                      <option value="usage">{t("tags.sort.usage")}</option>
                    </Input>
                  </Col>
                  {hasSelection && (
                    <Col md="auto" className="d-flex gap-2 flex-wrap">
                      <Button
                        color="soft-danger"
                        disabled={bulkBusy}
                        onClick={() => void runBulkDelete()}
                      >
                        {t("tags.bulkDelete.action", {
                          count: selectedIds.length,
                        })}
                      </Button>
                      <Button
                        color="soft-primary"
                        disabled={bulkBusy || selectedIds.length < 2}
                        onClick={() => {
                          setMergeTargetId("");
                          setMergeOpen(true);
                        }}
                      >
                        {t("tags.merge.action")}
                      </Button>
                    </Col>
                  )}
                  {hasSearch && (
                    <Col md="auto">
                      <Button
                        color="light"
                        onClick={() => {
                          setSearch("");
                          setAppliedSearch("");
                          setPage(1);
                        }}
                      >
                        {t("tags.search.clear")}
                      </Button>
                    </Col>
                  )}
                </Row>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : tags.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <h5>
                      {hasSearch
                        ? t("tags.empty.filteredTitle")
                        : t("tags.empty.title")}
                    </h5>
                    <p className="mb-0">
                      {hasSearch
                        ? t("tags.empty.filteredDescription")
                        : t("tags.empty.description")}
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-muted mb-2">
                      {t("tags.totalCount", { count: total })}
                    </p>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 40 }}>
                              <Input
                                type="checkbox"
                                checked={
                                  tags.length > 0 &&
                                  selectedIds.length === tags.length
                                }
                                onChange={(e) =>
                                  toggleSelectAll(e.target.checked)
                                }
                                aria-label={t("tags.selectAll")}
                              />
                            </th>
                            <th>{t("tags.col.name")}</th>
                            <th style={{ width: 120 }}>
                              {t("tags.col.contacts")}
                            </th>
                            <th style={{ width: 160 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {tags.map((tag) => (
                            <tr key={tag.id}>
                              <td>
                                <Input
                                  type="checkbox"
                                  checked={selectedIds.includes(tag.id)}
                                  onChange={(e) =>
                                    toggleSelected(tag.id, e.target.checked)
                                  }
                                  aria-label={t("tags.selectOne", {
                                    name: tag.name,
                                  })}
                                />
                              </td>
                              <td>{tag.name}</td>
                              <td className="text-muted">
                                {tag.contacts_count ?? "—"}
                              </td>
                              <td className="text-end">
                                <div className="d-flex gap-1 justify-content-end">
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    onClick={() => openEdit(tag)}
                                    aria-label={t("tags.rename")}
                                  >
                                    {t("tags.rename")}
                                  </Button>
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    onClick={() => setDeleting(tag)}
                                    aria-label={t("tags.remove")}
                                  >
                                    {t("tags.remove")}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {lastPage > 1 && (
                      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mt-3">
                        <span className="text-muted small">
                          {t("tags.pagination.label", {
                            page,
                            lastPage,
                            total,
                          })}
                        </span>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            {t("tags.pagination.prev")}
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page >= lastPage || loading}
                            onClick={() =>
                              setPage((p) => Math.min(lastPage, p + 1))
                            }
                          >
                            {t("tags.pagination.next")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <Form onSubmit={submit}>
          <ModalHeader toggle={() => setModalOpen(false)}>
            {editing ? t("tags.modal.renameTitle") : t("tags.modal.title")}
          </ModalHeader>
          <ModalBody>
            <Label htmlFor="tag-name">{t("tags.col.name")}</Label>
            <Input
              id="tag-name"
              value={name}
              invalid={!!fieldError}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
            {fieldError && <FormFeedback>{fieldError}</FormFeedback>}
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              color="light"
              onClick={() => setModalOpen(false)}
            >
              {t("tags.cancel")}
            </Button>
            <Button type="submit" color="primary" disabled={saving}>
              {saving ? <Spinner size="sm" /> : t("tags.save")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        isOpen={!!deleting}
        toggle={() => !deletingBusy && setDeleting(null)}
        centered
      >
        <ModalHeader toggle={() => !deletingBusy && setDeleting(null)}>
          {t("tags.delete.title")}
        </ModalHeader>
        <ModalBody>
          {deleting &&
            t("tags.confirmDelete", {
              name: deleting.name,
            })}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={deletingBusy}
            onClick={() => setDeleting(null)}
          >
            {t("tags.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={deletingBusy}
            onClick={() => void confirmDelete()}
          >
            {deletingBusy ? <Spinner size="sm" /> : t("tags.remove")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={mergeOpen}
        toggle={() => !bulkBusy && setMergeOpen(false)}
        centered
      >
        <ModalHeader toggle={() => !bulkBusy && setMergeOpen(false)}>
          {t("tags.merge.title")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">{t("tags.merge.help")}</p>
          <Label htmlFor="merge-target">{t("tags.merge.target")}</Label>
          <Input
            id="merge-target"
            type="select"
            value={mergeTargetId}
            onChange={(e) =>
              setMergeTargetId(
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
          >
            <option value="">{t("tags.merge.targetPlaceholder")}</option>
            {selectedTagsOnPage.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </Input>
          {mergePreviewLoading && (
            <div className="mt-3 text-center">
              <Spinner size="sm" color="primary" />
            </div>
          )}
          {mergePreviewData && !mergePreviewLoading && (
            <Alert color="info" className="mt-3 mb-0 py-2 small">
              <p className="mb-1">
                {t("tags.merge.previewUnique", {
                  count: mergePreviewData.unique_contacts_count,
                })}
              </p>
              {mergePreviewData.source_contacts_sum >
                mergePreviewData.unique_contacts_count && (
                <p className="mb-1 text-muted">
                  {t("tags.merge.previewOverlap", {
                    sum: mergePreviewData.source_contacts_sum,
                  })}
                </p>
              )}
              {mergeTargetTag && (mergeTargetTag.contacts_count ?? 0) > 0 && (
                <p className="mb-0 text-muted">
                  {t("tags.merge.previewTargetKeeps", {
                    count: mergeTargetTag.contacts_count ?? 0,
                    name: mergeTargetTag.name,
                  })}
                </p>
              )}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={bulkBusy}
            onClick={() => setMergeOpen(false)}
          >
            {t("tags.cancel")}
          </Button>
          <Button
            color="primary"
            disabled={bulkBusy || mergeTargetId === ""}
            onClick={() => void runMerge()}
          >
            {bulkBusy ? <Spinner size="sm" /> : t("tags.merge.confirm")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default TagsList;
